import { useLanguage } from "@/contexts/LanguageContext";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { Button } from "@/components/ui/button";
import { MessageCircle, X } from "lucide-react";
import { translations } from "@/translations";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { motion } from "framer-motion";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import { OptimizedPicture } from "@/components/OptimizedPicture";
import { PageCTASection } from "@/components/PageCTASection";
import { usePageMeta } from "@/hooks/usePageMeta";

type Lang = "es" | "en" | "pt" | "fr";

interface MetodoT {
  metaTitle: string;
  metaDescription: string;
  title: string;
  pitch: string;
  short: string;
  clavesTitle: string;
  claves: { title: string; text: string }[];
  noTitle: string;
  noes: { title: string; text: string }[];
  wordsTitle: string;
  wordsIntro: string;
  words: { term: string; text: string }[];
  montessoriNote: string;
}

const T: Record<Lang, MetodoT> = {
  es: {
    metaTitle: "El Método Gándara",
    metaDescription:
      "Qué es Gándara y qué no es: el bienestar primero, cada persona a su ritmo, trilingüe con el inglés como lengua principal, la naturaleza como aula y una comunidad, no un negocio.",
    title: "El Método Gándara",
    pitch:
      "En Gándara, cada niña y cada niño de 1 a 12 años crece feliz, con seguridad y a su ritmo: aprende haciendo, en la naturaleza y en tres lenguas, (con el inglés como lengua principal), junto a un equipo que cuida primero a la persona, para que descubra quién es y gane las herramientas de una vida plena.",
    short:
      "Una escuela internacional familiar, en la naturaleza, donde niñas y niños crecen felices y con seguridad y aprenden haciendo, a su ritmo y en tres idiomas.",
    clavesTitle: "Qué es Gándara (el modelo, en 6 claves)",
    claves: [
      {
        title: "El bienestar emocional de cada niña y cada niño es la prioridad.",
        text: "Queremos que de Gándara salgan crianzas felices y seguras: los cuidados en el centro (vínculo, autoestima, adaptación respetuosa, acompañamiento emocional con comunicación no violenta y escucha activa). En el día a día, acompañamiento emocional y académico van de la mano: el equilibrio emocional es lo que permite asumir el reto académico.",
      },
      {
        title: "Desarrollo integral de la persona, cada una a su ritmo.",
        text: "Escuela activa de inspiración Montessori: trabajo autónomo en ambientes preparados que despiertan el asombro, comunidades pequeñas y estables con tutoría de referencia, y una ratio que permite trato individualizado: potenciar las fortalezas de cada criatura y acompañar lo que está en desarrollo. La persona acompañante es guía, no transmisora.",
      },
      {
        title: "Trilingüe con el inglés como lengua principal.",
        text: "Las tres lenguas conviven de forma natural, sin abandonar el pensamiento abstracto y lógico.",
      },
      {
        title: "La naturaleza es aula.",
        text: "Entorno rural, huerta y viernes de Aula Natura en playa, bosque y río: el aprendizaje vivencial no es una excursión, es parte del método. Ser consciente de lo que me rodea me ayuda a conocerme a mí misma.",
      },
      {
        title: "Foco en el proceso, con estructura y evidencias.",
        text: "Enseñamos a aprender a aprender (miramos procesos, no solo resultados) dentro de un marco claro: currículo oficial de referencia y acreditación internacional (NEASC), Educación Cósmica como eje de proyectos, matemáticas y lectoescritura diarias, y una evaluación que acompaña sin clasificar.",
      },
      {
        title: "Una comunidad, no un negocio.",
        text: "Proyecto independiente y pequeño por diseño (~60 familias), sostenido por convicción, donde las familias son parte del proyecto y no clientas. El objetivo final no es una nota: es que cada niña y cada niño descubra quién es y construya una vida plena, con sus luces y sus sombras.",
      },
    ],
    noTitle: "Qué NO es Gándara",
    noes: [
      {
        title: "No es un colegio que compre resultados a costa del bienestar:",
        text: "ni el inglés, ni la acreditación, ni ninguna métrica pasan por delante de la seguridad emocional de una niña o un niño.",
      },
      {
        title: 'No es una escuela "libre" sin marco:',
        text: "libertad, no libertinaje: hay rutinas, currículo, metas y evaluación, y los límites se establecen con amor porque también son cuidado. Cuidar no es evitar toda frustración.",
      },
      {
        title: "No es un Montessori ortodoxo certificado:",
        text: "Montessori es la inspiración metodológica, integrada con un currículo internacional y decisiones propias. No vendemos pureza AMI.",
      },
      {
        title: "No es un colegio británico ni una academia de inglés:",
        text: "el inglés es vehículo de aprendizaje, no un fin publicitario; no prometemos bilingüismo instantáneo, prometemos progresión medida.",
      },
      {
        title: "No es una ludoteca en el bosque:",
        text: "lo vivencial tiene rigor detrás: programaciones, cortes de evaluación y acreditación externa.",
      },
      {
        title: "No es enseñanza tradicional:",
        text: "sin libros de texto como eje, sin deberes sistemáticos, sin notas numéricas ni rankings.",
      },
    ],
    wordsTitle: "Las palabras de Gándara",
    wordsIntro:
      "Estas son las palabras que oiréis (y que vuestras hijas e hijos ya usan), con lo que significan para nosotros:",
    words: [
      {
        term: "Acompañante.",
        text: 'La persona adulta que prepara el ambiente, presenta materiales, observa y acompaña. No decimos "profe" (y si vosotras lo decís, no pasa nada): el nombre cuenta el rol. Su intervención se retira poco a poco a medida que cada criatura gana autonomía.',
      },
      {
        term: "Ambiente.",
        text: 'No decimos "aula" ni "clase": un ambiente es un espacio preparado con intención (orden, belleza, materiales accesibles) para que el trabajo autónomo sea posible. Cuidarlo es tarea de todos, también de las niñas y los niños.',
      },
      {
        term: "Trabajo autónomo.",
        text: 'La franja central del día: cada cual gestiona su trabajo entre los materiales y propuestas a los que ya tiene acceso, a su ritmo y nivel. No es juego libre ni "cada uno a lo suyo": hay marco, propósito y una mirada adulta constante y discreta.',
      },
      {
        term: "Libertad.",
        text: "En Gándara la libertad no es el punto de partida: es una conquista. Se va ampliando conforme cada criatura demuestra autonomía y cuidado del material, del ambiente y de las demás personas. Libertad con responsabilidad, nunca libertinaje.",
      },
      {
        term: "Normas y límites.",
        text: "Las normas son acuerdos de convivencia que se construyen con el grupo: pocas, claras y en positivo. Los límites son las fronteras no negociables que protegen la seguridad física y emocional; los sostiene el equipo adulto con amor y con firmeza.",
      },
      {
        term: "Propuesta, reto, proyecto.",
        text: "Tres maneras de trabajar: la propuesta se ofrece y se puede aceptar o no; el reto se acepta y compromete (tiene meta visible); el proyecto se sostiene en el tiempo y suele terminar en algo que se presenta. Las propuestas nacen de observar a cada criatura, no de un temario.",
      },
      {
        term: "El papel: hojas de trabajo, cuadernos, porfolios.",
        text: "La experiencia va primero: se aprende haciendo, con material concreto, evolucionando progresivamente hacia lo abstracto. El papel llega después, para registrar, ordenar y consolidar lo vivido; y en el último ciclo la escritura gana peso a propósito.",
      },
      {
        term: "Evaluación y datos amables.",
        text: "Sin exámenes con nota, sin rankings. Registramos evidencias del aprendizaje real y hacemos cortes de evaluación con criterios comunes, para orientar la ayuda y celebrar el progreso, nunca para etiquetar ni comparar.",
      },
      {
        term: "Aula Natura.",
        text: "El viernes en que todo el cole aprende junto en playa, bosque o río. Con su marco de seguridad detrás, como todo lo vivencial en Gándara.",
      },
      {
        term: "Comunidad.",
        text: "Cada grupo estable de niñas y niños con sus personas acompañantes es una comunidad; y todos juntos, crianzas, familias y equipo, somos la comunidad educativa.",
      },
    ],
    montessoriNote:
      "La inspiración Montessori está en la base de todo esto: ambientes preparados, materiales concretos, mezcla de edades y respeto profundo por los ritmos de la infancia, integrada con un currículo internacional y decisiones propias.",
  },
  en: {
    metaTitle: "The Gándara Method",
    metaDescription:
      "What Gándara is and what it is not: wellbeing first, each person at their own pace, trilingual with English as the main language, nature as a classroom and a community, not a business.",
    title: "The Gándara Method",
    pitch:
      "At Gándara, every child from 1 to 12 grows up happy, safe and at their own pace: they learn by doing, in nature and in three languages (with English as the main language), alongside a team that cares for the person first, so that each child discovers who they are and gains the tools for a full life.",
    short:
      "A family-like international school in nature, where children grow up happy and safe and learn by doing, at their own pace and in three languages.",
    clavesTitle: "What Gándara is (the model, in 6 keys)",
    claves: [
      {
        title: "Each child's emotional wellbeing comes first.",
        text: "We want children to leave Gándara happy and secure: care is at the centre (bonding, self-esteem, respectful settling-in, emotional accompaniment with non-violent communication and active listening). In everyday life, emotional and academic accompaniment go hand in hand: emotional balance is what makes the academic challenge possible.",
      },
      {
        title: "Whole-person development, each at their own pace.",
        text: "An active school inspired by Montessori: independent work in carefully prepared learning environments that spark wonder, small and stable communities with a reference tutor, and a ratio that allows truly individual attention. The adult educator guides, rather than transmits.",
      },
      {
        title: "Trilingual, with English as the main language.",
        text: "The three languages live together naturally, without giving up abstract and logical thinking.",
      },
      {
        title: "Nature is a classroom.",
        text: "A rural setting, a vegetable garden and Aula Natura Fridays at the beach, in the forest and by the river: experiential learning is not a field trip, it is part of the method. Being aware of what surrounds me helps me get to know myself.",
      },
      {
        title: "Focus on the process, with structure and evidence.",
        text: "We teach how to learn to learn (we look at processes, not only results) within a clear framework: an official reference curriculum and international accreditation (NEASC), Cosmic Education as the backbone of our projects, daily mathematics and literacy, and assessment that accompanies without ranking.",
      },
      {
        title: "A community, not a business.",
        text: "An independent project, small by design (~60 families), sustained by conviction, where families are part of the project, not clients. The end goal is not a grade: it is for every child to discover who they are and build a full life, with its lights and its shadows.",
      },
    ],
    noTitle: "What Gándara is NOT",
    noes: [
      {
        title: "It is not a school that buys results at the cost of wellbeing:",
        text: "neither English, nor accreditation, nor any metric comes before a child's emotional safety.",
      },
      {
        title: 'It is not a "free" school without a framework:',
        text: "freedom, not licence: there are routines, a curriculum, goals and assessment, and limits are set with love because they are also care. Caring does not mean avoiding all frustration.",
      },
      {
        title: "It is not an orthodox certified Montessori school:",
        text: "Montessori is our methodological inspiration, combined with an international curriculum and our own decisions. We do not sell AMI purity.",
      },
      {
        title: "It is not a British school or an English academy:",
        text: "English is a vehicle for learning, not an advertising claim; we do not promise instant bilingualism, we promise measured progression.",
      },
      {
        title: "It is not a play centre in the woods:",
        text: "the experiential has rigour behind it: teaching programmes, assessment checkpoints and external accreditation.",
      },
      {
        title: "It is not traditional schooling:",
        text: "no textbooks as the backbone, no systematic homework, no number grades or rankings.",
      },
    ],
    wordsTitle: "The words of Gándara",
    wordsIntro:
      "These are the words you will hear (and that your children already use), and what they mean to us:",
    words: [
      {
        term: "Educator.",
        text: 'The adult who prepares the environment, presents materials, observes and accompanies. We do not say "teacher" (and if you do, that is fine): the name tells the role. Their intervention gradually steps back as each child gains autonomy.',
      },
      {
        term: "Learning environment.",
        text: 'We do not say "classroom": a learning environment is a space prepared with intention (order, beauty, accessible materials) so that independent work is possible. Caring for it is everyone\'s job, children included.',
      },
      {
        term: "Independent work.",
        text: 'The central part of the day: each child manages their own work among the materials and proposals they already have access to, at their own pace and level. It is not free play, nor "everyone on their own": there is a framework, a purpose and a constant, discreet adult presence.',
      },
      {
        term: "Freedom and access.",
        text: "At Gándara freedom is not the starting point: it is a conquest. It grows as each child shows autonomy and care for the materials, the environment and other people. Freedom with responsibility, never licence.",
      },
      {
        term: "Agreements and limits.",
        text: "Agreements are the shared rules of living together that each group builds: few, clear and stated in the positive. Limits are the non-negotiable boundaries that protect physical and emotional safety; the adult team holds them with love and with firmness.",
      },
      {
        term: "Proposal, challenge, project.",
        text: "Three ways of working: a proposal is offered and can be taken up or not; a challenge is accepted and commits you (it has a visible goal); a project is sustained over time and usually ends in something that is presented. Proposals are born from observing each child, not from a syllabus.",
      },
      {
        term: "Paper: worksheets, notebooks, portfolios.",
        text: "Experience comes first: children learn by doing, with concrete materials, progressively moving towards the abstract. Paper comes afterwards, to record, organise and consolidate what was lived; and in the last cycle writing deliberately gains weight.",
      },
      {
        term: "Assessment and supportive data.",
        text: "No graded exams, no rankings. We record evidence of real learning and hold assessment checkpoints with shared criteria, to guide support and celebrate progress, never to label or compare.",
      },
      {
        term: "Aula Natura.",
        text: "The Friday when the whole school learns together at the beach, in the forest or by the river. With its safety framework behind it, like everything experiential at Gándara.",
      },
      {
        term: "Community.",
        text: "Each stable group of children with their educators is a community; and all together (children, families and team) we are the school community.",
      },
    ],
    montessoriNote:
      "Montessori inspiration underpins all of this: prepared environments, concrete materials, mixed ages and deep respect for childhood rhythms, combined with an international curriculum and our own decisions.",
  },
  pt: {
    metaTitle: "O Método Gándara",
    metaDescription:
      "O que é Gándara e o que não é: o bem-estar primeiro, cada pessoa ao seu ritmo, trilingue com o inglês como língua principal, a natureza como sala e uma comunidade, não um negócio.",
    title: "O Método Gándara",
    pitch:
      "Em Gándara, cada menina e cada menino de 1 a 12 anos cresce feliz, com segurança e ao seu ritmo: aprende fazendo, na natureza e em três línguas (com o inglês como língua principal), junto a uma equipa que cuida primeiro da pessoa, para que descubra quem é e ganhe as ferramentas de uma vida plena.",
    short:
      "Uma escola internacional familiar, na natureza, onde meninas e meninos crescem felizes e com segurança e aprendem fazendo, ao seu ritmo e em três idiomas.",
    clavesTitle: "O que é Gándara (o modelo, em 6 chaves)",
    claves: [
      {
        title: "O bem-estar emocional de cada criança é a prioridade.",
        text: "Queremos que de Gándara saiam crianças felizes e seguras: os cuidados no centro (vínculo, autoestima, adaptação respeitosa, acompanhamento emocional com comunicação não violenta e escuta ativa). No dia a dia, acompanhamento emocional e académico andam de mãos dadas: o equilíbrio emocional é o que permite assumir o desafio académico.",
      },
      {
        title: "Desenvolvimento integral da pessoa, cada uma ao seu ritmo.",
        text: "Escola ativa de inspiração Montessori: trabalho autónomo em ambientes preparados que despertam o assombro, comunidades pequenas e estáveis com tutoria de referência, e um rácio que permite atenção individualizada. A pessoa acompanhante é guia, não transmissora.",
      },
      {
        title: "Trilingue com o inglês como língua principal.",
        text: "As três línguas convivem de forma natural, sem abandonar o pensamento abstrato e lógico.",
      },
      {
        title: "A natureza é sala de aula.",
        text: "Meio rural, horta e sextas-feiras de Aula Natura na praia, no bosque e no rio: a aprendizagem vivencial não é uma excursão, é parte do método. Ser consciente do que me rodeia ajuda-me a conhecer-me a mim mesma.",
      },
      {
        title: "Foco no processo, com estrutura e evidências.",
        text: "Ensinamos a aprender a aprender (olhamos processos, não só resultados) dentro de um marco claro: currículo oficial de referência e acreditação internacional (NEASC), Educação Cósmica como eixo de projetos, matemática e literacia diárias, e uma avaliação que acompanha sem classificar.",
      },
      {
        title: "Uma comunidade, não um negócio.",
        text: "Projeto independente e pequeno por desenho (~60 famílias), sustentado por convicção, onde as famílias são parte do projeto e não clientes. O objetivo final não é uma nota: é que cada criança descubra quem é e construa uma vida plena, com as suas luzes e as suas sombras.",
      },
    ],
    noTitle: "O que Gándara NÃO é",
    noes: [
      {
        title: "Não é uma escola que compre resultados à custa do bem-estar:",
        text: "nem o inglês, nem a acreditação, nem nenhuma métrica passam à frente da segurança emocional de uma criança.",
      },
      {
        title: 'Não é uma escola "livre" sem marco:',
        text: "liberdade, não libertinagem: há rotinas, currículo, metas e avaliação, e os limites estabelecem-se com amor porque também são cuidado. Cuidar não é evitar toda a frustração.",
      },
      {
        title: "Não é um Montessori ortodoxo certificado:",
        text: "Montessori é a inspiração metodológica, integrada com um currículo internacional e decisões próprias. Não vendemos pureza AMI.",
      },
      {
        title: "Não é um colégio britânico nem uma academia de inglês:",
        text: "o inglês é veículo de aprendizagem, não um fim publicitário; não prometemos bilinguismo instantâneo, prometemos progressão medida.",
      },
      {
        title: "Não é uma ludoteca no bosque:",
        text: "o vivencial tem rigor por trás: programações, cortes de avaliação e acreditação externa.",
      },
      {
        title: "Não é ensino tradicional:",
        text: "sem manuais como eixo, sem trabalhos de casa sistemáticos, sem notas numéricas nem rankings.",
      },
    ],
    wordsTitle: "As palavras de Gándara",
    wordsIntro:
      "Estas são as palavras que ouvirão (e que as vossas filhas e filhos já usam), com o que significam para nós:",
    words: [
      {
        term: "Acompanhante.",
        text: 'A pessoa adulta que prepara o ambiente, apresenta materiais, observa e acompanha. Não dizemos "professor" (e se vocês o disserem, não faz mal): o nome conta o papel. A sua intervenção retira-se pouco a pouco à medida que cada criança ganha autonomia.',
      },
      {
        term: "Ambiente.",
        text: 'Não dizemos "sala de aula": um ambiente é um espaço preparado com intenção (ordem, beleza, materiais acessíveis) para que o trabalho autónomo seja possível. Cuidar dele é tarefa de todos, também das crianças.',
      },
      {
        term: "Trabalho autónomo.",
        text: 'A faixa central do dia: cada um gere o seu trabalho entre os materiais e propostas a que já tem acesso, ao seu ritmo e nível. Não é jogo livre nem "cada um por si": há marco, propósito e um olhar adulto constante e discreto.',
      },
      {
        term: "Liberdade.",
        text: "Em Gándara a liberdade não é o ponto de partida: é uma conquista. Vai-se ampliando conforme cada criança demonstra autonomia e cuidado do material, do ambiente e das outras pessoas. Liberdade com responsabilidade, nunca libertinagem.",
      },
      {
        term: "Normas e limites.",
        text: "As normas são acordos de convivência que se constroem com o grupo: poucas, claras e em positivo. Os limites são as fronteiras não negociáveis que protegem a segurança física e emocional; sustenta-os a equipa adulta com amor e com firmeza.",
      },
      {
        term: "Proposta, desafio, projeto.",
        text: "Três maneiras de trabalhar: a proposta oferece-se e pode aceitar-se ou não; o desafio aceita-se e compromete (tem meta visível); o projeto sustenta-se no tempo e costuma terminar em algo que se apresenta. As propostas nascem de observar cada criança, não de um temário.",
      },
      {
        term: "O papel: folhas de trabalho, cadernos, portfólios.",
        text: "A experiência vai primeiro: aprende-se fazendo, com material concreto, evoluindo progressivamente para o abstrato. O papel chega depois, para registar, ordenar e consolidar o vivido; e no último ciclo a escrita ganha peso de propósito.",
      },
      {
        term: "Avaliação e dados amáveis.",
        text: "Sem exames com nota, sem rankings. Registamos evidências da aprendizagem real e fazemos cortes de avaliação com critérios comuns, para orientar a ajuda e celebrar o progresso, nunca para etiquetar nem comparar.",
      },
      {
        term: "Aula Natura.",
        text: "A sexta-feira em que toda a escola aprende junta na praia, no bosque ou no rio. Com o seu marco de segurança por trás, como tudo o vivencial em Gándara.",
      },
      {
        term: "Comunidade.",
        text: "Cada grupo estável de crianças com as suas pessoas acompanhantes é uma comunidade; e todos juntos (crianças, famílias e equipa) somos a comunidade educativa.",
      },
    ],
    montessoriNote:
      "A inspiração Montessori está na base de tudo isto: ambientes preparados, materiais concretos, mistura de idades e respeito profundo pelos ritmos da infância, integrada com um currículo internacional e decisões próprias.",
  },
  fr: {
    metaTitle: "La méthode Gándara",
    metaDescription:
      "Ce qu'est Gándara et ce qu'elle n'est pas : le bien-être d'abord, chacun à son rythme, trilingue avec l'anglais comme langue principale, la nature comme salle de classe et une communauté, pas un commerce.",
    title: "La méthode Gándara",
    pitch:
      "À Gándara, chaque enfant de 1 à 12 ans grandit heureux, en sécurité et à son rythme : il apprend en faisant, dans la nature et en trois langues (avec l'anglais comme langue principale), aux côtés d'une équipe qui prend soin d'abord de la personne, pour qu'il découvre qui il est et gagne les outils d'une vie pleine.",
    short:
      "Une école internationale familiale, dans la nature, où les enfants grandissent heureux et en sécurité et apprennent en faisant, à leur rythme et en trois langues.",
    clavesTitle: "Ce qu'est Gándara (le modèle, en 6 clés)",
    claves: [
      {
        title: "Le bien-être émotionnel de chaque enfant est la priorité.",
        text: "Nous voulons que les enfants sortent de Gándara heureux et sûrs d'eux : le soin au centre (lien, estime de soi, adaptation respectueuse, accompagnement émotionnel avec communication non violente et écoute active). Au quotidien, accompagnement émotionnel et académique vont de pair : l'équilibre émotionnel est ce qui permet de relever le défi académique.",
      },
      {
        title: "Développement intégral de la personne, chacune à son rythme.",
        text: "École active d'inspiration Montessori : travail autonome dans des ambiances préparées qui éveillent l'émerveillement, communautés petites et stables avec un tutorat de référence, et un ratio qui permet un accompagnement individualisé. L'adulte est un guide, pas un transmetteur.",
      },
      {
        title: "Trilingue avec l'anglais comme langue principale.",
        text: "Les trois langues cohabitent naturellement, sans renoncer à la pensée abstraite et logique.",
      },
      {
        title: "La nature est une salle de classe.",
        text: "Cadre rural, potager et vendredis d'Aula Natura à la plage, en forêt et au bord de la rivière : l'apprentissage par l'expérience n'est pas une sortie, c'est une partie de la méthode. Être conscient de ce qui m'entoure m'aide à me connaître moi-même.",
      },
      {
        title: "Le processus d'abord, avec structure et preuves.",
        text: "Nous apprenons à apprendre (nous regardons les processus, pas seulement les résultats) dans un cadre clair : programme officiel de référence et accréditation internationale (NEASC), Éducation Cosmique comme axe des projets, mathématiques et littératie quotidiennes, et une évaluation qui accompagne sans classer.",
      },
      {
        title: "Une communauté, pas un commerce.",
        text: "Projet indépendant et petit par choix (~60 familles), soutenu par conviction, où les familles font partie du projet et ne sont pas des clientes. Le but final n'est pas une note : c'est que chaque enfant découvre qui il est et construise une vie pleine, avec ses lumières et ses ombres.",
      },
    ],
    noTitle: "Ce que Gándara N'EST PAS",
    noes: [
      {
        title: "Ce n'est pas une école qui achète des résultats au prix du bien-être :",
        text: "ni l'anglais, ni l'accréditation, ni aucune métrique ne passent avant la sécurité émotionnelle d'un enfant.",
      },
      {
        title: 'Ce n\'est pas une école « libre » sans cadre :',
        text: "liberté, pas licence : il y a des routines, un programme, des objectifs et une évaluation, et les limites se posent avec amour parce qu'elles sont aussi du soin. Prendre soin, ce n'est pas éviter toute frustration.",
      },
      {
        title: "Ce n'est pas un Montessori orthodoxe certifié :",
        text: "Montessori est l'inspiration méthodologique, intégrée à un programme international et à des décisions propres. Nous ne vendons pas de pureté AMI.",
      },
      {
        title: "Ce n'est pas une école britannique ni une académie d'anglais :",
        text: "l'anglais est un véhicule d'apprentissage, pas un argument publicitaire ; nous ne promettons pas un bilinguisme instantané, nous promettons une progression mesurée.",
      },
      {
        title: "Ce n'est pas une ludothèque dans les bois :",
        text: "l'expérientiel a de la rigueur derrière lui : programmations, points d'évaluation et accréditation externe.",
      },
      {
        title: "Ce n'est pas un enseignement traditionnel :",
        text: "pas de manuels comme colonne vertébrale, pas de devoirs systématiques, pas de notes chiffrées ni de classements.",
      },
    ],
    wordsTitle: "Les mots de Gándara",
    wordsIntro:
      "Voici les mots que vous entendrez (et que vos enfants utilisent déjà), et ce qu'ils signifient pour nous :",
    words: [
      {
        term: "Accompagnant.",
        text: "L'adulte qui prépare l'ambiance, présente les matériels, observe et accompagne. Nous ne disons pas « maître » ou « maîtresse » (et si vous le dites, ce n'est pas grave) : le nom dit le rôle. Son intervention se retire peu à peu à mesure que chaque enfant gagne en autonomie.",
      },
      {
        term: "Ambiance.",
        text: "Nous ne disons pas « salle de classe » : une ambiance est un espace préparé avec intention (ordre, beauté, matériels accessibles) pour que le travail autonome soit possible. En prendre soin est la tâche de tous, enfants compris.",
      },
      {
        term: "Travail autonome.",
        text: "Le cœur de la journée : chacun gère son travail parmi les matériels et propositions auxquels il a déjà accès, à son rythme et à son niveau. Ce n'est ni du jeu libre ni « chacun pour soi » : il y a un cadre, un but et une présence adulte constante et discrète.",
      },
      {
        term: "Liberté.",
        text: "À Gándara la liberté n'est pas le point de départ : c'est une conquête. Elle s'élargit à mesure que chaque enfant montre de l'autonomie et du soin envers le matériel, l'ambiance et les autres. Liberté avec responsabilité, jamais licence.",
      },
      {
        term: "Règles et limites.",
        text: "Les règles sont des accords de vie commune construits avec le groupe : peu nombreuses, claires et formulées en positif. Les limites sont les frontières non négociables qui protègent la sécurité physique et émotionnelle ; l'équipe adulte les tient avec amour et fermeté.",
      },
      {
        term: "Proposition, défi, projet.",
        text: "Trois façons de travailler : la proposition s'offre et peut être acceptée ou non ; le défi s'accepte et engage (il a un but visible) ; le projet se poursuit dans le temps et se termine souvent par quelque chose qui se présente. Les propositions naissent de l'observation de chaque enfant, pas d'un programme.",
      },
      {
        term: "Le papier : fiches de travail, cahiers, portfolios.",
        text: "L'expérience vient d'abord : on apprend en faisant, avec du matériel concret, en évoluant progressivement vers l'abstrait. Le papier vient ensuite, pour consigner, ordonner et consolider le vécu ; et au dernier cycle l'écriture prend du poids, à dessein.",
      },
      {
        term: "Évaluation et données bienveillantes.",
        text: "Pas d'examens notés, pas de classements. Nous recueillons des preuves de l'apprentissage réel et faisons des points d'évaluation avec des critères communs, pour orienter l'aide et célébrer les progrès, jamais pour étiqueter ni comparer.",
      },
      {
        term: "Aula Natura.",
        text: "Le vendredi où toute l'école apprend ensemble à la plage, en forêt ou au bord de la rivière. Avec son cadre de sécurité derrière, comme tout l'expérientiel à Gándara.",
      },
      {
        term: "Communauté.",
        text: "Chaque groupe stable d'enfants avec ses accompagnants est une communauté ; et tous ensemble (enfants, familles et équipe) nous sommes la communauté éducative.",
      },
    ],
    montessoriNote:
      "L'inspiration Montessori est à la base de tout cela : ambiances préparées, matériels concrets, mélange des âges et respect profond des rythmes de l'enfance, intégrée à un programme international et à des décisions propres.",
  },
};

const Montessori = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const m = T[(["es", "en", "pt", "fr"].includes(language) ? language : "es") as Lang];
  usePageMeta({
    title: `${m.metaTitle} – Gándara School`,
    description: m.metaDescription,
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner />
      <div className="container mx-auto px-4 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="max-w-4xl mx-auto mb-12 text-center">
            <h1 className="text-5xl font-light text-primary mb-6 animate-fade-in">
              {m.title}
            </h1>

            <div className="flex justify-center mb-10">
              <Button
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
                size="lg"
                className="gap-2 shadow-lg hover:shadow-xl transition-all flex items-center"
              >
                <MessageCircle className="h-5 w-5" />
                <span>{t.montessori.bookVisit}</span>
              </Button>
            </div>

            <div className="bg-muted/50 border-l-4 border-primary rounded-r-xl p-6 md:p-8 text-left mb-6">
              <p className="text-lg md:text-xl leading-relaxed text-foreground font-medium m-0">
                {m.pitch}
              </p>
            </div>
            <p className="text-lg italic text-accent-foreground/80 font-medium max-w-2xl mx-auto">
              {m.short}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-light text-primary mb-8 text-center">{m.clavesTitle}</h2>
            <div className="space-y-5 mb-16">
              {m.claves.map((clave, i) => (
                <motion.div
                  key={clave.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="flex gap-4 bg-card p-6 rounded-xl shadow-md"
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    {i + 1}
                  </div>
                  <p className="m-0 text-muted-foreground">
                    <strong className="text-foreground">{clave.title}</strong> {clave.text}
                  </p>
                </motion.div>
              ))}
            </div>

            <h2 className="text-3xl font-light text-primary mb-8 text-center">{m.noTitle}</h2>
            <div className="space-y-4 mb-16">
              {m.noes.map((no) => (
                <div key={no.title} className="flex gap-3">
                  <X className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                  <p className="m-0 text-muted-foreground">
                    <strong className="text-foreground">{no.title}</strong> {no.text}
                  </p>
                </div>
              ))}
            </div>

            <div className="my-12">
              <OptimizedPicture
                src="/lovable-uploads/montessori-division-guia.jpg"
                alt="Colegio Montessori cerca de Vigo en Gondomar"
                width={600}
                height={900}
                loading="lazy"
                decoding="async"
                className="w-full max-w-[600px] h-auto mx-auto rounded-xl shadow-lg"
              />
            </div>

            <h2 className="text-3xl font-light text-primary mb-4 text-center">{m.wordsTitle}</h2>
            <p className="text-lg text-muted-foreground text-center mb-8">{m.wordsIntro}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
              {m.words.map((word) => (
                <div key={word.term} className="bg-card p-5 rounded-xl shadow-sm">
                  <p className="m-0 text-muted-foreground text-base">
                    <strong className="text-primary">{word.term}</strong> {word.text}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-base text-muted-foreground italic text-center max-w-2xl mx-auto mb-16">
              {m.montessoriNote}
            </p>

            <div className="flex justify-center mb-4">
              <Button
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                <span>{t.montessori.bookVisit}</span>
              </Button>
            </div>

            <div className="my-12">
              <OptimizedPicture
                src="/lovable-uploads/12c7dfb1-5567-43e8-82a4-3500c43ed5e5.png"
                alt="Espacio Montessori cerca de Vigo en Gondomar"
                width={600}
                height={400}
                loading="lazy"
                decoding="async"
                className="w-full max-w-[600px] h-auto mx-auto rounded-xl shadow-lg"
              />
            </div>

            <div className="prose max-w-4xl mx-auto text-muted-foreground space-y-8">
              <h2 className="text-3xl font-light text-foreground mt-4 mb-8 text-center">{t.montessori.space.title}</h2>
              <p className="text-lg">{t.montessori.space.description1}</p>
              <p className="text-lg">{t.montessori.space.description2}</p>
              <p className="text-lg">{t.montessori.space.description3}</p>
              <p className="text-lg"><strong>{t.montessori.space.property}</strong></p>
              <p className="text-lg"><strong>{t.montessori.space.facilities}</strong></p>
              <p className="text-lg">{t.montessori.space.location}</p>

              {/* Classrooms Section */}
              <section className="py-8">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-3xl font-light text-primary mb-6">{t.montessori.classrooms.title}</h2>
                  <p className="text-lg text-muted-foreground mb-12">{t.montessori.classrooms.description}</p>

                  <div className="mb-12">
                    <h3 className="text-2xl font-light text-primary mb-4">{t.montessori.infantil.title}</h3>
                    <p className="text-muted-foreground mb-4">{t.montessori.infantil.description}</p>
                    <p className="text-muted-foreground">{t.montessori.infantil.classroom}</p>
                  </div>

                  <div className="mb-12">
                    <h3 className="text-2xl font-light text-primary mb-4">{t.montessori.primary.title}</h3>
                    <p className="text-muted-foreground mb-4">{t.montessori.primary.description}</p>
                    <p className="text-muted-foreground">{t.montessori.primary.classroom}</p>
                  </div>
                </div>
              </section>

              {/* Nature & Active Learning Section */}
              <section className="py-8 bg-accent/5 rounded-2xl px-8 mb-12">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-3xl font-light text-primary mb-6">{t.montessori.nature.title}</h2>
                  <p className="text-lg text-muted-foreground">{t.montessori.nature.description}</p>
                </div>
              </section>

              <PageCTASection variant="accent" />
            </div>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default Montessori;
